import {FC} from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import {IGartnerPlotsData} from "../../../../pages/Dashboard/Dashboard.type";
import {
  GARTNER_CHART_COLORS,
  GARTNER_CURRENT_COMPLETED_YEAR,
  GARTNER_HISTOGRAM_FILL_OPACITY,
} from "./constants";
import {buildLifecycleChartData} from "./utils";

interface IProps {
  plotsData: IGartnerPlotsData;
}

const LegendDot: FC<{color: string; label: string; dashed?: boolean}> = ({
  color,
  label,
  dashed = false,
}) => (
  <div style={{display: "flex", alignItems: "center", gap: 6}}>
    <span
      style={{
        width: 14,
        height: dashed ? 0 : 3,
        borderTop: dashed ? `2px dashed ${color}` : undefined,
        backgroundColor: dashed ? "transparent" : color,
        display: "inline-block",
      }}
    />
    <span style={{color: "#D1D5DB", fontSize: 11}}>{label}</span>
  </div>
);

const LifecycleTooltip: FC<TooltipProps<number, string>> = ({
  active,
  payload,
  label,
}) => {
  if (!active || !payload?.length) return null;

  const items = payload.filter((item) => {
    if (item.value === null || item.value === undefined) return false;
    const year = Number(label);
    if (
      year === GARTNER_CURRENT_COMPLETED_YEAR &&
      item.dataKey === "h_t_forecast"
    ) {
      return false;
    }
    if (
      year > GARTNER_CURRENT_COMPLETED_YEAR &&
      (item.dataKey === "scienceHist" || item.dataKey === "patentsHist")
    ) {
      return false;
    }
    return true;
  });

  return (
    <div
      style={{
        backgroundColor: "rgba(34, 39, 43, 0.95)",
        border: `1px solid ${GARTNER_CHART_COLORS.panelBorder}`,
        borderRadius: 8,
        color: "#fff",
        fontSize: 12,
        padding: "8px 10px",
      }}
    >
      <div style={{marginBottom: 6}}>{`Год: ${label}`}</div>
      {items.map((item) => (
        <div key={item.dataKey} style={{color: item.color}}>
          {`${item.name}: ${item.value}`}
        </div>
      ))}
    </div>
  );
};

export const GartnerLifecycleChart: FC<IProps> = ({plotsData}) => {
  const data = buildLifecycleChartData(plotsData);

  return (
    <div
      style={{
        border: `1px solid ${GARTNER_CHART_COLORS.panelBorder}`,
        borderRadius: "16px",
        background:
          "linear-gradient(180deg, rgba(34, 39, 43, 0.95) 0%, rgba(17, 20, 24, 0.95) 100%)",
        padding: "16px",
      }}
    >
      <div
        style={{
          color: "#FFFFFF",
          fontSize: "16px",
          fontWeight: 600,
          marginBottom: "12px",
        }}
      >
        Нормированные показатели
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "12px",
          marginBottom: "12px",
        }}
      >
        <LegendDot
          color={GARTNER_CHART_COLORS.scienceBar}
          label="Гистограмма: количество научных статей"
        />
        <LegendDot
          color={GARTNER_CHART_COLORS.patentsBar}
          label="Гистограмма: количество патентов"
        />
        <LegendDot color={GARTNER_CHART_COLORS.h_t_fact} label="Кривая: факт" />
        <LegendDot
          color={GARTNER_CHART_COLORS.h_t_forecast}
          label="Кривая: прогноз"
          dashed
        />
      </div>

      <div style={{width: "100%", height: 280}}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{top: 22, right: 8, left: 0, bottom: 8}}>
            <CartesianGrid
              stroke={GARTNER_CHART_COLORS.grid}
              vertical={false}
            />
            <XAxis
              dataKey="year"
              tick={{fill: GARTNER_CHART_COLORS.axis, fontSize: 10}}
              axisLine={{stroke: GARTNER_CHART_COLORS.grid}}
              tickLine={false}
            />
            <YAxis
              tick={{fill: GARTNER_CHART_COLORS.axis, fontSize: 10}}
              axisLine={false}
              tickLine={false}
              width={36}
            />
            <Tooltip content={<LifecycleTooltip />} />
            <Bar
              dataKey="scienceHist"
              fill={GARTNER_CHART_COLORS.scienceBar}
              fillOpacity={GARTNER_HISTOGRAM_FILL_OPACITY}
              barSize={10}
              name="Научные статьи"
            />
            <Bar
              dataKey="patentsHist"
              fill={GARTNER_CHART_COLORS.patentsBar}
              fillOpacity={GARTNER_HISTOGRAM_FILL_OPACITY}
              barSize={10}
              name="Патенты"
            />
            <Line
              type="monotone"
              dataKey="scienceCurve"
              stroke={GARTNER_CHART_COLORS.scienceCurve}
              dot={false}
              strokeWidth={2}
              name="Кривая научных статей"
            />
            <Line
              type="monotone"
              dataKey="patentsCurve"
              stroke={GARTNER_CHART_COLORS.patentsCurve}
              dot={false}
              strokeWidth={2}
              name="Кривая патентов"
            />
            <Line
              type="monotone"
              dataKey="h_t_fact"
              stroke={GARTNER_CHART_COLORS.h_t_fact}
              dot={false}
              strokeWidth={2.5}
              connectNulls={false}
              name="H(t) факт"
            />
            <Line
              type="monotone"
              dataKey="h_t_forecast"
              stroke={GARTNER_CHART_COLORS.h_t_forecast}
              dot={false}
              strokeWidth={2.5}
              strokeDasharray="6 4"
              connectNulls={false}
              name="H(t) прогноз"
            />
            <ReferenceLine
              x={GARTNER_CURRENT_COMPLETED_YEAR}
              stroke={GARTNER_CHART_COLORS.referenceLine}
              strokeDasharray="4 4"
              isFront
              label={{
                value: String(GARTNER_CURRENT_COMPLETED_YEAR),
                position: "top",
                fill: GARTNER_CHART_COLORS.referenceLine,
                fontSize: 11,
                offset: 6,
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
